import * as React from 'react';
import * as $ from 'jquery';

import { IGlob } from '../../../_common/application/ts/Glob';
import NumberField from '../../../_common/platform/tsx/NumberField';
import AttrQueryFilterField from '../../../_common/platform/tsx/filtering/AttrQueryFilterField';
import BooleanField from '../../../_common/platform/tsx/BooleanField';
import StringField from '../../../_common/platform/tsx/StringField';
import { FormatChooser, IFormatChooserValue } from '../../../_common/platform/tsx/FormatChooser';

import { isMinMaxAggregate } from '../../../_common/platform/ts/AttrQueryValuesFormat';

import AttributeDesigner, {
    ITreeItem,
    IToggleStates
} from '../../../_common/platform/tsx/attributeDesigner/AttributeDesigner';

import SelectField from '../../../_common/platform/tsx/SelectField';
import { DataSourceSelect } from '../../../_common/platform/tsx/DataSourceSelectField';
import ParameterFieldBase, { IParameterFieldBaseOptions } from '../../../_common/platform/ts/ParameterFieldBase';

import { ReorderButton } from '../../../_common/platform/tsx/ReorderButton';
import { RemoveButton } from '../../../_common/platform/tsx/RemoveButton';

import { IExpression } from '../../../pdc-platform/src/ts/stores/EntityModelsStore';

import { validators as AppValidators } from '../../../_common/application/ts/validators';

import {
    IQueryFilter,
    IAttrQueryBase,
    IGlobalFilterControl,
    IAttrQueryFilterFieldValue
} from '../../../_common/platform/tsx/filtering/Interfaces';

import { IValuesCollection } from '../../../_common/platform/ts/appTheme';
import * as ConfigurationConstants from '../../../_common/application/ts/ConfigurationConstants';
import { ATTRIBUTE_TYPES } from '../../platform/ts/ConfigurationConstants';
import { QueryRootEntityField } from '../../platform/tsx/QueryRootEntityField';
import { useElementsReorderingHook } from '../../platform/ts/DragDropCommon';

interface IAttrQuery extends IAttrQueryBase {
    distinct?: boolean;
    limit?: number;
    separator?: string;
}

interface ISubValueTypeQuery extends IAttrQuery {
    subValueType?: string;
}

interface IValue {
    dataSourceID?: number;
    attribute: IAttrQuery;
    filter?: IQueryFilter[];
    globalFilterControl?: IGlobalFilterControl;
    title?: string; //LL: 18.07.18, used if showTitle
    queryRootEntity?: string;
}

export interface IAttrQueryKPIFieldComponentOptions extends IParameterFieldBaseOptions {
    hideFormatSetting?: boolean; // hides "Format" column   default false
    singleRow?: boolean;
    hasLabel?: boolean; // default false
    allowNoneAggregate?: boolean;
    showTitle?: boolean; //LL: 18.07.18, default false
    hideValueType?: boolean; //LL: 18.07.18, default false   hides "Value Type" column
    htmlContainerTheme?: any; // Theme form the HTMLContainer when the KPI is used as AQL Data plugin
}

interface IAttrQueryKPIFieldComponentProperties {
    value: IValue[];
    dataSourceID: number;
    tabID?: number;
    theme?: IValuesCollection;
    options: IAttrQueryKPIFieldComponentOptions;
    glob: IGlob;

    onChange(value: IValue[]): void;
}

function AttrQueryKPIFieldRow(props: {
    value?: ISubValueTypeQuery;
    theme: IValuesCollection;
    dataSourceID: number;
    index?: number;
    entitiesTree?: ITreeItem[];
    entityCollapsedDict?: IToggleStates;
    isEmpty?: boolean;
    hasLabel?: boolean;
    singleRow?: boolean;
    isMultiDataSourceMode?: boolean;
    allowNoneAggregate?: boolean;
    filter?: IQueryFilter[];
    globalFilterControl?: IGlobalFilterControl;
    queryRootEntity?: string;
    hideFormatSetting?: boolean;
    hideValueType?: boolean; //LL: 18.07.18, default false
    showTitle?: boolean; //LL: 18.07.18, default false
    title?: string; //LL: 18.07.18, used if showTitle
    tabID: number;
    nItems?: number; //LL: 13.01.20: to show ReorderButton
    glob: IGlob;

    onChange?(value: IAttrQuery, index: number): void;
    onAddRow?(obj): void;
    onRemove?(index: number): void;
    onEntityCollapsedDictChange?(value: IToggleStates): void;
    onChangeFilter?(value: IAttrQueryFilterFieldValue): void;
    onChangeDataSource?(value: number): void;
    onQueryRootEntityChange?(value: string): void;
    onChangeTitle?(value: string): void; //LL: 19.07.18, used if showTitle
}) {
    const normalize = (value: IAttrQuery) => {
        if (value.attribute == null && value.expression == null) {
            delete value.aggregateGroup;
            delete value.distinct;
            delete value.label;
            delete value.formatFront;
            delete value.currency;
            delete value.separator;
            delete value.formatAsType;
            return;
        }

        if (value.label != null && value.label.length === 0) {
            delete value.label;
        }

        if (value.separator != null && value.separator.length === 0) {
            delete value.separator;
        }

        const attributeType = props.glob.EntityModelsStore.getAttributeType(value, props.dataSourceID);

        if (attributeType == null) {
            return; //VR: why this "return"? We should handle Agregation, Format here.
        }

        if (value.formatAsType != null && attributeType !== ATTRIBUTE_TYPES.ANYTYPE) {
            delete value.formatAsType;
        }

        //LL: Default aggregation: for Entities - count, for numbers - sum
        if (value.aggregateGroup == null) {
            if (attributeType === ATTRIBUTE_TYPES.INTEGER || attributeType === ATTRIBUTE_TYPES.REAL) {
                value.aggregateGroup = 'sum';
            } else {
                value.aggregateGroup = 'count';
            }
        }

        const aggregateList = getAggregateList(value, attributeType);
        const aggregateGroupIsFound = aggregateList.find((val) => {
            return val === value.aggregateGroup;
        });

        if (aggregateGroupIsFound == null || aggregateGroupIsFound === 'group') {
            delete value.aggregateGroup;
        }
    };

    const getAggregateList = (value: IAttrQuery, attributeType: string) => {
        let result = ConfigurationConstants.AGGREGATE_GROUP_VALUES[attributeType];

        if ((value != null && value.expression != null) || props.allowNoneAggregate) {
            result = ['none'].concat(result);
        }

        return result;
    };

    const getValueCopy = () => {
        return $.extend(true, {}, props.value);
    };

    const handleOnAttributeChange = (values: string[]) => {
        const valueCopy = getValueCopy();
        const newAttribute = values[0];
        const oldAttributeType = props.glob.EntityModelsStore.getAttributeType(valueCopy.attribute, props.dataSourceID);
        const newAttributeType = props.glob.EntityModelsStore.getAttributeType(newAttribute, props.dataSourceID);

        if (newAttributeType !== oldAttributeType) {
            delete valueCopy.intervals;
            delete valueCopy.formatAsType;
        }

        delete valueCopy.expression;
        valueCopy.attribute = newAttribute;
        processValueChange(valueCopy);
    };

    const handleOnExpressionChange = (expression: IExpression) => {
        const valueCopy = getValueCopy();

        valueCopy.expression = expression;
        delete valueCopy.attribute;
        processValueChange(valueCopy);
    };

    const handleOnAggregateChange = (value: string) => {
        const valueCopy = getValueCopy();

        // if (!props.allowNoneAggregate && value === 'none') {
        //     delete valueCopy.aggregateGroup;
        // } else {
        valueCopy.aggregateGroup = value;
        //}
        processValueChange(valueCopy);
    };

    const handleDistinctChange = (value: boolean) => {
        const valueCopy = getValueCopy();

        valueCopy.distinct = value;
        processValueChange(valueCopy);
    };

    const handleOnLimitChange = (value: number) => {
        const valueCopy = getValueCopy();

        valueCopy.limit = value;
        processValueChange(valueCopy);
    };

    const handleOnFormatFrontChange = (value: IFormatChooserValue) => {
        const valueCopy = getValueCopy();

        if (value.formatFront == null) {
            delete valueCopy.formatFront;
            delete valueCopy.currency;
        } else {
            valueCopy.formatFront = value.formatFront;
            valueCopy.customFormat = value.customFormat;
            valueCopy.currency = value.currency;
        }

        if (value.formatAsType != null) {
            valueCopy.formatAsType = value.formatAsType;
        } else {
            delete valueCopy.formatAsType;
        }

        valueCopy.precision = value.precision;
        valueCopy.useDigitGrouping = value.useDigitGrouping;
        valueCopy.overwriteInheritedUseDigitGrouping = value.overwriteInheritedUseDigitGrouping;
        valueCopy.formatZeroAsBlank = value.formatZeroAsBlank;
        valueCopy.overwriteInheritedFormatZeroAsBlank = value.overwriteInheritedFormatZeroAsBlank;
        valueCopy.useFiscalPrefix = value.useFiscalPrefix;
        valueCopy.fiscalYearDivider = value.fiscalYearDivider;
        valueCopy.cellStyle = value.cellStyle;
        valueCopy.template = value.template;

        processValueChange(valueCopy);
    };

    const handleOnLabelChange = (value: string) => {
        const valueCopy = getValueCopy();

        valueCopy.label = value;
        processValueChange(valueCopy);
    };

    const handleOnSeparatorChange = (value: string) => {
        const valueCopy = getValueCopy();

        valueCopy.separator = value;
        processValueChange(valueCopy);
    };

    const handleOnSubValueTypeChange = (value) => {
        const valueCopy = getValueCopy();

        valueCopy.subValueType = value;
        processValueChange(valueCopy);
    };

    const processValueChange = (value: IAttrQuery) => {
        normalize(value);

        if (typeof props.onChange === 'function') {
            props.onChange(value, props.index);
        }
    };

    const handleOnRemove = () => {
        if (typeof props.onRemove === 'function') {
            props.onRemove(props.index);
        }
    };

    const getAggregate = (value: IAttrQuery, dataSourceID: number) => {
        if (value.attribute == null && value.expression == null) {
            return;
        }

        const attributeType = props.glob.EntityModelsStore.getAttributeType(value, dataSourceID);
        const displayVal = value.aggregateGroup;
        const aggregateList = getAggregateList(value, attributeType);
        const options = aggregateList?.map((value) => {
            if (value === 'group') {
                return;
            }

            return (
                <option key={value} value={value}>
                    {value}
                </option>
            );
        });

        return (
            <SelectField value={displayVal} onChange={handleOnAggregateChange}>
                {options}
            </SelectField>
        );
    };

    const getDistinct = (value: IAttrQuery) => {
        if (value.attribute == null && value.expression == null) {
            return;
        }

        return <BooleanField title={''} value={value.distinct} allowNull={false} onChange={handleDistinctChange} />;
    };

    const getLabel = (value: IAttrQuery) => {
        if (value.attribute == null && value.expression == null) {
            return;
        }

        let placeholder: string;

        if (typeof value.attribute === 'string') {
            placeholder = props.glob.EntityModelsStore.getReferredAttributeLabel(value.attribute, props.dataSourceID);
        }

        return <StringField value={value.label || ''} placeholder={placeholder} onChange={handleOnLabelChange} />;
    };

    const getLimit = (value: IAttrQuery) => {
        if (value.attribute == null && value.expression == null) {
            return;
        }

        if (value.limit == undefined) {
            value.limit = 0;
        }

        return <NumberField value={value.limit} onChange={handleOnLimitChange} />;
    };

    const getSeparator = (value: IAttrQuery) => {
        if (value.attribute == null && value.expression == null) {
            return;
        }

        return <StringField value={value.separator || ''} onChange={handleOnSeparatorChange} />;
    };

    const getSubValueType = (value: ISubValueTypeQuery) => {
        if (value.attribute == null && value.expression == null) {
            return;
        }

        let typeOptions = {
            values: ['main', 'sub', 'hide'],
            display: ['Main Value', 'Subvalue', 'Hidden']
        };

        return (
            <SelectField
                value={value.subValueType || 'sub'}
                options={typeOptions}
                onChange={handleOnSubValueTypeChange}
            />
        );
    };

    // new design (from Chart Config):
    const isFictiv = () => {
        return props.isEmpty === true;
    };

    const handleOnAddFresh = (e) => {
        if (typeof props.onAddRow === 'function') {
            props.onAddRow({});
        }
    };

    const getAddDataQuery = () => {
        if (!props.singleRow) {
            const displayVal = (
                <button type="button" className="btn btn-default btn-sm" onClick={handleOnAddFresh}>
                    {'Add Data Query'}
                </button>
            );

            return (
                <div className="entity-select test-add-new">
                    <div className="content">
                        <span className="value">{displayVal}</span>
                    </div>
                </div>
            );
        }
    };

    const getBlock = () => {
        const value = props.value || ({} as ISubValueTypeQuery);
        const aggregateGroup = value.aggregateGroup || 'count';

        const style1 = {
            marginBottom: 14,
            width: '100%'
        };
        const style2 = { borderTop: '0px' }; // { borderTop: 'solid 1px gray' };

        let dataSourceID = props.glob.EntityModelsStore.getStoredDataSourceID();

        if (props.dataSourceID != null) {
            dataSourceID = props.dataSourceID;
        }

        let titleElement: React.JSX.Element;

        if (props.showTitle) {
            titleElement = (
                <div className="col-sm-4">
                    <label>{'Title'}</label>
                    <StringField value={props.title || ''} onChange={props.onChangeTitle} />
                </div>
            );
        }

        let limitComponentHeader;
        let limitComponentRow;

        if (!props.hasLabel) {
            limitComponentHeader = [
                <th key="group1" className="cell-group-aggregate"></th>,
                <th key="empty1" className="cell-empty-space"></th>
            ];

            limitComponentRow = [
                <th key="group2" className="cell-group-aggregate"></th>,
                <th key="empty2" className="cell-empty-space"></th>
            ];

            //props.allowNoneAggregate
            if (aggregateGroup === 'none') {
                limitComponentHeader = [
                    <th key="group1" className="cell-group-aggregate">
                        {'Limit'}
                    </th>,
                    <th key="separ1" className="cell-label">
                        {'Separator'}
                    </th>
                ];

                limitComponentRow = [
                    <th key="group2" className="cell-group-aggregate">
                        {getLimit(value)}
                    </th>,
                    <th key="separ2" className="cell-label">
                        {getSeparator(value)}
                    </th>
                ];
            } else {
                value.limit = 0; // ? VR: is it needed for aggregation != none?
            }
        }

        let formatComponentHeader: React.JSX.Element;
        let formatComponentRow: React.JSX.Element;

        const attributeType = props.glob.EntityModelsStore.getAttributeType(value, props.dataSourceID);

        if (attributeType != null && (!props.hideFormatSetting || isMinMaxAggregate(value))) {
            let customizeFormat = true;

            if (
                (value.aggregateGroup == 'min' || value.aggregateGroup == 'max') &&
                attributeType === ATTRIBUTE_TYPES.STRING
            ) {
                customizeFormat = false;
            } else {
                customizeFormat = attributeType !== ATTRIBUTE_TYPES.STRING;
            }

            formatComponentHeader = <th className="cell-formatFront">{'Format'}</th>;
            formatComponentRow = (
                <td className="cell-formatFront">
                    <FormatChooser
                        isScalar={true}
                        value={value}
                        theme={props.theme}
                        customizeFormat={customizeFormat}
                        showStyle={true}
                        showTemplate={false}
                        showRawTemplate={false}
                        dataSourceID={dataSourceID}
                        glob={props.glob}
                        onChange={handleOnFormatFrontChange}
                    />
                </td>
            );
        }

        //VR: add SubValue Indicator Type:
        let subValueType_Header;
        let subValueType_Row;

        if (props.hideFormatSetting && props.hideValueType !== true) {
            subValueType_Header = <th className="cell-subValueType">{'Value Type'}</th>;
            subValueType_Row = <td className="cell-subValueType">{getSubValueType(value)}</td>;
        }

        const filter = props.filter || [];
        const validEntities = props.glob.EntityModelsStore.getValidEntitiesForMultipeRootFunctionality(value, filter);

        let attribute: string | IExpression;

        if (value.expression != null) {
            attribute = value.expression;
        } else {
            attribute = value.attribute;
        }

        let nItems = props.nItems || 0;

        return (
            <table className="table" style={style1}>
                <tbody>
                    <tr>
                        <td className="cell-remove"></td>
                        <td colSpan={1} className="row0-container container">
                            <div className="row">
                                <div className="col-sm-3">
                                    <DataSourceSelect
                                        value={dataSourceID}
                                        glob={props.glob}
                                        onChange={props.onChangeDataSource}
                                    />
                                </div>

                                <div className="col-sm-3">
                                    <QueryRootEntityField
                                        value={props.queryRootEntity}
                                        dataSourceID={dataSourceID}
                                        glob={props.glob}
                                        onChange={props.onQueryRootEntityChange}
                                    />
                                </div>

                                {titleElement}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className="cell-remove">
                            <RemoveButton onClick={() => handleOnRemove()} />
                            <ReorderButton nItems={nItems} />
                        </td>

                        <td colSpan={1} className="dataDescriptor-container" style={style2}>
                            <table style={{ width: '100%' }} className="table test-kpi-row">
                                <thead>
                                    <tr>
                                        <th className="cell-attribute">{'Attribute'}</th>
                                        <th className="cell-group-aggregate">{'Aggregation'}</th>
                                        <th className="cell-distinct">{'Distinct'}</th>
                                        {formatComponentHeader}
                                        {limitComponentHeader}
                                        {props.hasLabel ? <th className="cell-label">{'Label'}</th> : null}
                                        {subValueType_Header}
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr>
                                        <td className="cell-attribute">
                                            <AttributeDesigner
                                                tree={props.entitiesTree}
                                                dataSourceID={dataSourceID}
                                                modes={['attribute', 'expression']}
                                                value={attribute}
                                                collapsedDict={props.entityCollapsedDict}
                                                validEntities={validEntities}
                                                tabID={props.tabID}
                                                glob={props.glob}
                                                onChange={handleOnAttributeChange}
                                                onExpressionChange={handleOnExpressionChange}
                                                onCollapsedDictChange={props.onEntityCollapsedDictChange}
                                            />
                                        </td>
                                        <td className="cell-group-aggregate">{getAggregate(value, dataSourceID)}</td>
                                        <td className="cell-distinct">{getDistinct(value)}</td>
                                        {formatComponentRow}
                                        {limitComponentRow}
                                        {props.hasLabel ? <td className="cell-label">{getLabel(value)}</td> : null}
                                        {subValueType_Row}
                                    </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>

                    <tr>
                        <td></td>
                        <td colSpan={1} className="filter-container">
                            <AttrQueryFilterField
                                value={{
                                    filters: filter,
                                    globalFilterControl: props.globalFilterControl
                                }}
                                dataSourceID={dataSourceID}
                                validEntities={validEntities}
                                theme={props.theme}
                                tabID={props.tabID}
                                glob={props.glob}
                                onChange={props.onChangeFilter}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    };

    if (isFictiv()) {
        const addDataQuery = getAddDataQuery();

        return (
            <tr className="attribute-row">
                <td className="cell-remove"></td>
                <td colSpan={1}>{addDataQuery}</td>
            </tr>
        );
    }

    const block = getBlock();

    return (
        <tr className="attribute-row">
            <td colSpan={2}>{block}</td>
        </tr>
    );
}

export function AttrQueryKPIFieldComponent(props: IAttrQueryKPIFieldComponentProperties) {
    props.glob.EntityModelsStore.useLoadModelsHook();
    const [entityCollapsedDict, setEntityCollapsedDict] = React.useState<IToggleStates>({});
    const tableRef = React.useRef<HTMLTableSectionElement>(null);
    useElementsReorderingHook(
        tableRef,
        {
            axis: 'y',
            sortableItems: 'tr.attribute-row:not(.empty)',
            handle: '.dx-icon-dragvertical'
        },
        (start, stop) => {
            let value = [...props.value];

            const item = value.splice(start, 1);
            value.splice(stop, 0, item[0]);

            if (typeof props.onChange === 'function') {
                props.onChange(value);
            }
        },
        [props.value]
    );

    const handleValueChange = (value: IValue[]) => {
        if (typeof props.onChange === 'function') {
            props.onChange(value);
        }
    };

    const handleEntityCollapsedDictChange = (value) => {
        setEntityCollapsedDict(value);
    };

    const getValueCopy = () => {
        return [...props.value];
    };

    const addRow = (obj) => {
        const valueCopy = getValueCopy();
        const attributeType = props.glob.EntityModelsStore.getAttributeType(obj.attribute, props.dataSourceID);

        if (attributeType === ATTRIBUTE_TYPES.INTEGER || attributeType === ATTRIBUTE_TYPES.REAL) {
            obj.aggregateGroup = 'sum';
        } else {
            obj.aggregateGroup = 'count';
        }

        let dataSourceID = props.glob.EntityModelsStore.getStoredDataSourceID();

        if (valueCopy.length > 0 && valueCopy[valueCopy.length - 1].dataSourceID != null) {
            dataSourceID = valueCopy[valueCopy.length - 1].dataSourceID;
        }

        valueCopy.push({
            attribute: obj,
            filter: [],
            dataSourceID: dataSourceID
        });
        handleValueChange(valueCopy);
    };

    const changeRow = (obj: IAttrQuery, i: number) => {
        const valueCopy = getValueCopy();

        valueCopy[i].attribute = obj;
        handleValueChange(valueCopy);
    };

    const getOnChangeFiltersHandler = (i: number) => {
        return (value: IAttrQueryFilterFieldValue) => {
            const valueCopy = getValueCopy();

            valueCopy[i].filter = value.filters;
            valueCopy[i].globalFilterControl = value.globalFilterControl;
            handleValueChange(valueCopy);
        };
    };

    const getOnQueryRootEntityChangeHandler = (i: number) => {
        return (value: string) => {
            const valueCopy = getValueCopy();

            valueCopy[i].queryRootEntity = value;
            handleValueChange(valueCopy);
        };
    };

    const getOnDataSourceChangeHandler = (i: number) => {
        return (newDataSourceID: number) => {
            props.glob.EntityModelsStore.storeDataSourceID(newDataSourceID);

            const result = [...props.value];
            const value = result[i];

            value.dataSourceID = newDataSourceID;

            //LL: emptify Data Source Descriptor:
            value.attribute = {};
            value.filter = [];
            handleValueChange(result);
        };
    };

    const getOnChangeTitleHandler = (i: number) => {
        return (newTitle: string) => {
            const valueCopy = getValueCopy();

            valueCopy[i].title = newTitle;
            handleValueChange(valueCopy);
        };
    };

    const removeRow = (i: number) => {
        const valueCopy = getValueCopy();

        valueCopy.splice(i, 1);
        handleValueChange(valueCopy);
    };

    const { value, options } = props;
    const hideFormatSetting = options != null && options.hideFormatSetting === true;
    const attrQueryStore = props.glob.EntityModelsStore;
    const showTitle = options != null && options.showTitle === true; //LL: 18.07.18, default false
    const hideValueType = options != null && options.hideValueType === true; //LL: 18.07.18, default false

    let theme = props.theme;
    let tabID = props.tabID;

    if (options != null) {
        if (options.htmlContainerTheme != null) {
            theme = options.htmlContainerTheme;
        } else if (theme == null) {
            theme = options.theme;
        }

        if (options.tabID != null) {
            tabID = options.tabID;
        }
    }

    let nItems = value.length; //LL: 13.01.20: to show ReorderButton

    const rows = value?.map((obj, index) => {
        const entitiesTree = attrQueryStore.getEntitiesTreeByDS(obj.dataSourceID);

        // See WI 101342. Visual reoredring does not work if key is not unique or is not changed during reordering.
        return (
            <AttrQueryKPIFieldRow
                key={index + '-' + obj.attribute}
                index={index}
                singleRow={options.singleRow}
                hasLabel={options.hasLabel}
                allowNoneAggregate={options.allowNoneAggregate}
                entitiesTree={entitiesTree}
                value={obj.attribute}
                theme={theme}
                hideFormatSetting={hideFormatSetting}
                hideValueType={hideValueType}
                tabID={tabID}
                nItems={nItems}
                showTitle={showTitle}
                title={obj.title}
                onChangeTitle={getOnChangeTitleHandler(index)}
                dataSourceID={obj.dataSourceID}
                queryRootEntity={obj.queryRootEntity}
                filter={obj.filter}
                globalFilterControl={obj.globalFilterControl}
                glob={props.glob}
                onChangeDataSource={getOnDataSourceChangeHandler(index)}
                onQueryRootEntityChange={getOnQueryRootEntityChangeHandler(index)}
                onChangeFilter={getOnChangeFiltersHandler(index)}
                onChange={changeRow}
                onRemove={removeRow}
                entityCollapsedDict={entityCollapsedDict}
                onEntityCollapsedDictChange={handleEntityCollapsedDictChange}
            />
        );
    });

    //LL: 14.05.17:
    let dataSourceID_for_fictiv = attrQueryStore.getStoredDataSourceID();

    if (value.length > 0 && value[value.length - 1].dataSourceID != null) {
        dataSourceID_for_fictiv = value[value.length - 1].dataSourceID;
    }

    return (
        <div className="attrquery-kpi-field">
            <table className="table">
                <tbody ref={tableRef}>
                    {rows}
                    {options.singleRow && value.length > 0 ? null : (
                        <AttrQueryKPIFieldRow
                            key={rows.length + '_fictiv'}
                            isEmpty={true}
                            theme={theme}
                            dataSourceID={dataSourceID_for_fictiv}
                            tabID={props.tabID}
                            glob={props.glob}
                            onAddRow={addRow}
                        />
                    )}
                </tbody>
            </table>
        </div>
    );
}

const kpiValidators = {
    attributes_notEmpty: function (value) {
        var find = value.find((obj) => {
            return (obj.attribute == null || obj.attribute.attribute == null) && obj.attribute.expression == null;
        });

        if (find != null) {
            return ['Empty Attributes are prohibited'];
        }
    },

    localFilter: function (value) {
        const numberOfAttributes = value != null ? value.length : 0;

        if (numberOfAttributes === 0) {
            return null;
        }

        for (let i = 0; i < numberOfAttributes; i++) {
            const filter1 = value[i].filter;
            const resString = AppValidators.localFilter.validate(filter1);

            if (resString != null) {
                return resString;
            }
        }
    }
};

export class AttrQueryKPIField extends ParameterFieldBase<IAttrQueryKPIFieldComponentOptions> {
    public static defaultValidators = [kpiValidators.attributes_notEmpty, kpiValidators.localFilter];

    public static EditComponent = AttrQueryKPIFieldComponent;
    public static getDefaultValue() {
        return [];
    }

    constructor(glob, title: string, validators?: any, defaultVal?: any, options?: IAttrQueryKPIFieldComponentOptions) {
        if (defaultVal == null) {
            defaultVal = AttrQueryKPIField.getDefaultValue();
        }

        super(glob, title, AttrQueryKPIField.defaultValidators, validators, defaultVal, options);
    }
}
